import { marked } from "marked";
import React, { memo, useMemo } from "react";
import ReactMarkdown from "react-markdown";

/** Parse markdown into blocks for memoization during streaming. */
const parseMarkdownIntoBlocks = (markdown: string): string[] => {
	const tokens = marked.lexer(markdown);
	return tokens.map((token) => token.raw);
};

type MemoizedMarkdownBlockProps = {
	content: string;
	className?: string;
};

/** Memoized markdown block - only re-renders when content changes. */
const MemoizedMarkdownBlock = memo(
	({ content, className }: MemoizedMarkdownBlockProps) => {
		return (
			<ReactMarkdown
				className={`prose prose-sm prose-neutral max-w-none ${className ?? ""}`.trim()}
				components={{
					// Style headings
					h1: ({ children }) => <h1 className="mb-2 mt-2 text-lg font-semibold">{children}</h1>,
					h2: ({ children }) => <h2 className="mb-2 mt-2 text-base font-semibold">{children}</h2>,
					h3: ({ children }) => <h3 className="mb-2 mt-2 text-sm font-semibold">{children}</h3>,
					// Style paragraphs
					p: ({ children }) => <p className="mb-2 leading-[16px]">{children}</p>,
					// Style lists
					ul: ({ children }) => <ul className="mb-2 list-disc space-y-1 pl-4">{children}</ul>,
					ol: ({ children }) => <ol className="mb-2 list-decimal space-y-1 pl-4">{children}</ol>,
					li: ({ children }) => <li className="leading-[16px]">{children}</li>,
					// Style inline elements
					strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
					em: ({ children }) => <em className="italic">{children}</em>,
					// Style code
					code: ({ children, className }) => {
						const isInline = !className;
						return isInline ? (
							<code className="rounded bg-neutral-200 px-1 py-0.5 font-mono text-xs">{children}</code>
						) : (
							<code className={className}>{children}</code>
						);
					},
					pre: ({ children }) => (
						<pre className="mb-2 overflow-x-auto rounded-lg bg-neutral-100 p-3 text-xs">{children}</pre>
					),
					// Style links
					a: ({ href, children }) => (
						<a
							href={href}
							className="text-primary-5 hover:underline"
							target="_blank"
							rel="noopener noreferrer"
						>
							{children}
						</a>
					),
					// Style blockquotes
					blockquote: ({ children }) => (
						<blockquote className="mb-2 border-l-2 border-neutral-300 pl-3 italic text-neutral-600">
							{children}
						</blockquote>
					),
				}}
			>
				{content}
			</ReactMarkdown>
		);
	},
	(prevProps, nextProps) => prevProps.content === nextProps.content
);

MemoizedMarkdownBlock.displayName = "MemoizedMarkdownBlock";

type MemoizedMarkdownProps = {
	content: string;
	id: string;
	className?: string;
};

/** Memoized Markdown optimized for streaming - only changed blocks re-render. */
export const MemoizedMarkdown = memo(({ content, id, className }: MemoizedMarkdownProps) => {
	const blocks = useMemo(() => parseMarkdownIntoBlocks(content), [content]);

	return (
		<div
			className={`text-sm leading-[16px] text-neutral-900 [&>:first-child>:first-child]:!mt-0 [&>:last-child>:last-child]:!mb-0 ${className ?? ""}`.trim()}
		>
			{blocks.map((block, index) => (
				<MemoizedMarkdownBlock content={block} key={`${id}-block_${index}`} />
			))}
		</div>
	);
});

MemoizedMarkdown.displayName = "MemoizedMarkdown";

export default MemoizedMarkdown;
